import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const ORG_ROLES = ['client', 'venue_owner', 'vendor', 'partner'];

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'client':
      return 'badge-client';
    case 'venue_owner':
      return 'badge-venue';
    case 'vendor':
      return 'badge-vendor';
    case 'partner':
      return 'badge-partner';
    default:
      return '';
  }
};

const Organizations = () => {
  usePageTitle('Organizations');
  const { isViewer } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [sortBy, setSortBy] = useState('organization_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const navigate = useNavigate();

  useEffect(() => {
    // Reset to page 1 when search or filter changes
    setPage(1);
  }, [search, roleFilter]);

  useEffect(() => {
    loadOrganizations();
  }, [search, roleFilter, page, limit, sortBy, sortOrder]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (search) params.search = search;
      if (roleFilter && roleFilter !== 'all') params.role = roleFilter;
      
      const response = await organizationsAPI.list(params);
      
      // Handle new paginated response format
      if (response.data.data && response.data.pagination) {
        setOrganizations(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old response format
        setOrganizations(response.data);
        setPagination({
          total: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    // Generate page numbers to show
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        {/* Info */}
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> organizations
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Rows per page */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(1)}
              disabled={!has_prev}
              data-testid="first-page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(page - 1)}
              disabled={!has_prev}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pageNum)}
                  data-testid={`page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(page + 1)}
              disabled={!has_next}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(total_pages)}
              disabled={!has_next}
              data-testid="last-page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="organizations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            Organizations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your clients, venue owners, vendors, and partners
          </p>
        </div>
        {!isViewer && (
          <Button asChild data-testid="add-organization-btn">
            <Link to="/organizations/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Organization
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="search-organizations"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50" data-testid="role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ORG_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role.replace('_', ' ').charAt(0).toUpperCase() + role.replace('_', ' ').slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : organizations.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('organization_name')}
                    >
                      <div className="flex items-center">
                        Name
                        {getSortIcon('organization_name')}
                      </div>
                    </TableHead>
                    <TableHead className="data-table">Roles</TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('account_manager_name')}
                    >
                      <div className="flex items-center">
                        Account Manager
                        {getSortIcon('account_manager_name')}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org) => (
                    <TableRow
                      key={org.id}
                      className="border-border/50 hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/organizations/${org.id}`)}
                      data-testid={`org-row-${org.id}`}
                    >
                      <TableCell className="font-medium">{org.organization_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {org.org_roles.map((role) => (
                            <Badge
                              key={role}
                              variant="outline"
                              className={`text-xs ${getRoleBadgeClass(role)}`}
                            >
                              {role.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {org.account_manager_name || org.account_manager || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No organizations found</p>
              <p className="text-sm mt-1">Add your first organization to get started</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/organizations/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Organization
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Organizations;
